/**
 * mainDashApi.js — API functions for the V2 Main Dashboard.
 *
 * Uses direct fetch to VITE_MY_REPORTS_ENDPOINT (matching V1 ApiRequester
 * "get-mysql-report" pattern) to ensure all required body fields are sent.
 * apiReport() only maps a subset of fields which drops dashboard-specific
 * params like active_filter — so we use direct fetch for reliability.
 */

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => ({
    'Content-Type': 'application/json',
    'x-amz-security-token': import.meta.env.VITE_AUTH_TOKEN,
});

const DEVICE_CAT_FILTER = '1,2,15,16';

async function dashFetch(body) {
    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(body),
        });
        const raw = await resp.json();
        if (raw?.Status?.toLowerCase() === 'ok') return raw.Res;
        if (raw?.Res) return raw.Res;
        return raw || {};
    } catch (err) {
        console.error('[mainDashApi] fetch error:', err);
        return {};
    }
}

function buildBody(repType, campusId, startDate, endDate, areaTypeFilter, extra = {}) {
    return {
        rep_type: repType,
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        area_type_filter: areaTypeFilter || '',
        device_category_filter: DEVICE_CAT_FILTER,
        ...extra,
    };
}

export function fetchDashboardStats(campusId, startDate, endDate, areaTypeFilter) {
    return dashFetch(buildBody('dashboardstatsV2', campusId, startDate, endDate, areaTypeFilter));
}

export function fetchResidentSummary(campusId, startDate, endDate, areaTypeFilter) {
    return dashFetch(buildBody('dashboardresidentsummaryV2', campusId, startDate, endDate, areaTypeFilter, { active_filter: true }));
}

export function fetchCaregiverSummary(campusId, startDate, endDate, areaTypeFilter) {
    return dashFetch(buildBody('dashboardcaregiversummaryV2', campusId, startDate, endDate, areaTypeFilter));
}

export function fetchAreaSummary(campusId, startDate, endDate, areaTypeFilter) {
    return dashFetch(buildBody('dashboardareasummaryV2', campusId, startDate, endDate, areaTypeFilter));
}

export function fetchAreaShiftSummary(campusId, startDate, endDate, areaTypeFilter) {
    return dashFetch(buildBody('dashboardareashiftsummary', campusId, startDate, endDate, areaTypeFilter));
}

export function fetchReasonData(campusId, startDate, endDate, areaTypeFilter) {
    return dashFetch(buildBody('dashboardreasons', campusId, startDate, endDate, areaTypeFilter));
}

export function fetchHourChartData(campusId, startDate, endDate, areaTypeFilter) {
    return dashFetch(buildBody('dashboardhourchartV2', campusId, startDate, endDate, areaTypeFilter));
}

/**
 * Fetch area type filter options.
 */
export async function fetchDashboardFilterOptions(campusId) {
    const data = await dashFetch({
        rep_type: 'getportalfilters',
        campus_id: campusId,
        area_type_filter: '1',
    });
    return data?.area_types || [];
}
